import { TextField, TextFieldProps, Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { color } from '@/colors';
import ShowMoreChip from '@/components/Nodes/Shared/ShowMoreChip';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';

const MAX_HEIGHT = 430;
interface NodeTextFieldProps extends Omit<TextFieldProps, 'expandable'> {
  expandable?: boolean;
  readOnly?: boolean;
  enableMouseWheelScroll?: boolean;
  maxHeight?: number;
}

export const NodeTextField = ({
  enableMouseWheelScroll = false,
  sx,
  slotProps,
  color: textColor,
  expandable = false,
  maxHeight = MAX_HEIGHT,
  ...props
}: NodeTextFieldProps) => {
  const textFieldRef = useRef<HTMLDivElement>(null);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useCaptureElementWheelScroll(enableMouseWheelScroll ? textFieldRef : { current: null });

  useEffect(() => {
    const handleHeightChange = (height) => {
      const containerHeight = maxHeight;
      setHasOverflow(height > containerHeight);
    };
    if (textFieldRef?.current) {
      const textFieldElement = textFieldRef?.current?.querySelector('.MuiInputBase-root');
      if (textFieldElement) {
        const height = textFieldElement.scrollHeight;
        handleHeightChange(height);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  return (
    <Box
      sx={{ position: 'relative', width: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="nodrag"
    >
      <TextField
        ref={textFieldRef}
        {...props}
        slotProps={{
          ...slotProps,
          input: {
            ...(slotProps?.input || {}),
            style: {
              resize: 'vertical',
              fontSize: '16px',
              fontWeight: 500,
              color: textColor || color.White100,
            },
          },
        }}
        sx={{
          maxHeight: expandable || enableMouseWheelScroll ? (isTextExpanded ? 'auto' : `${maxHeight}px`) : 'none',
          overflow: 'auto',
          bgcolor: color.Black84,
          borderColor: color.White04_T,
          borderRadius: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            padding: '24px',
            '& fieldset': {
              borderColor: color.White04_T,
            },
            '&:hover fieldset': {
              borderColor: color.White04_T,
            },
            '&.Mui-focused fieldset': {
              borderColor: color.White04_T,
            },
            '& textarea::placeholder': {
              color: color.White40_T,
            },
          },
          ...sx,
        }}
      />
      {expandable && hasOverflow && (
        <ShowMoreChip isExpanded={isTextExpanded} showChip={isHovered} setIsExpanded={setIsTextExpanded} />
      )}
    </Box>
  );
};
